import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { ContactForm } from '../components/ContactForm'
import { TestimonialsSlider } from '../components/TestimonialsSlider'
import { useCMSSide, useCMSCollection } from '../hooks/useCMS'

export function ForloebPage() {
  const { side: cmsSide } = useCMSSide('forloeb')
  const { data: cmsForloeb } = useCMSCollection('forloeb')

  useSEO({
    title: cmsSide?.seo_titel || 'Forløb - BoSteendahl.dk',
    description: cmsSide?.seo_beskrivelse || 'Hos Bo Steendahl tilbydes behandlingsforløb for at arbejde i dybden med årsagerne frem for kun at lindre symptomer. Læs om processen og forundersøgelsen.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/20241001-091341632-ios.png',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Behandlingsforløb
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'JEG TILBYDER BEHANDLINGSFORLØB'}
          </h1>
          <p className="mt-4 text-lg sm:text-xl lg:text-2xl font-serif text-[#1b3a4b]/90 max-w-3xl mx-auto leading-relaxed">
            {cmsSide?.hero_underoverskrift || '- og kun enkeltbehandlinger i helt særlige tilfælde som svær angst, og som opfølgning på forløb.'}
          </p>
        </div>
      </section>

      {/* Rationale & Depth */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 relative z-20">
        <div className="bg-[#172e3b] text-white rounded-2xl p-8 sm:p-10 shadow-xl border border-white/10 space-y-4">
          <div className="flex items-center gap-2 text-[#7c875a] text-xs font-semibold uppercase tracking-wider">
            <i className="fa-solid fa-arrow-down-long" aria-hidden="true" />
            <span>HVORFOR KUN FORLØB?</span>
          </div>
          <h2 className="font-serif text-[21px] sm:text-3xl font-bold text-white">
            Arbejd i dybden med de underliggende årsager.
          </h2>
          <p className="text-sm sm:text-base text-[#FAF8F5]/90 leading-relaxed">
            Jeg tilbyder kun forløb, fordi varig forandring kræver mere end at dæmpe symptomerne. Vi skal finde de underliggende årsager bag det, du mærker. Uanset om det er følelsesmæssige blokeringer, fordøjelsesproblemer, søvnbesvær, lavt selvværd, uhensigtsmæssige mønstre eller at du har svært ved at sætte grænser, kræver det, at vi arbejder under overfladen. Det tager tid, og derfor skal vi sammen skabe de omstændigheder, der gør forandringen mulig, så den kan slå rod i kroppen, i dine tanker og i måden, du agerer på i hverdagen.
          </p>
          <p className="text-sm sm:text-base text-[#FAF8F5]/90 leading-relaxed">
            Et forløb hos mig er for dig, der ønsker hjælp til at bearbejde mental og følelsesmæssig stress, som mærkes som ubalance, tankemylder eller smerter i kroppen. Det er for dig, der vil slippe det, der holder dig tilbage, og som ønsker en tilgang, hvor krop og sind ses som en helhed.
          </p>
        </div>
      </section>

      {/* Forløbets 4 Faser */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-10">
              {/* Fase 1 */}
              <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
                <div>
                  <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">Trin 1</span>
                  <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">Før forundersøgelsen (spørgeskema)</h3>
                </div>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Når du booker tid til forundersøgelse, modtager du en mail med et link til et spørgeskema, som du bedes udfylde, før du møder op.
                </p>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Formålet med spørgeskemaet er, at jeg kan forberede mig bedst muligt, inden vi ses. Du beskriver de fysiske og psykiske symptomer, der fylder mest, hvor længe du har haft dem, og hvilken hjælp du eventuelt har fået tidligere. Du fortæller også, hvad du søger hjælp til, hvorfor netop nu, og lidt om dine familiære relationer og din hverdag. På den måde har jeg allerede et godt billede af din situation, når du kommer, og vi kan bruge tiden på det, der betyder mest for dig.
                </p>
              </div>

              {/* Fase 2 */}
              <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
                <div>
                  <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">Trin 2</span>
                  <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">Forundersøgelse</h3>
                </div>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Ethvert forløb starter med en grundig forundersøgelse, hvor vi gennemgår dine udfordringer og de oplevelser, der har ført dig derhen, hvor du er i dag. Forundersøgelsen er delt op i to besøg.
                </p>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Ved første besøg tager vi udgangspunkt i dine svar fra spørgeskemaet og uddyber dem sammen. Vi taler om, hvordan du hidtil har bearbejdet dine oplevelser, og hvilken effekt det har haft. Har du tidligere modtaget professionel hjælp, taler vi også om, hvordan du har oplevet den måde at arbejde med dig selv på. Derudover kommer vi ind på din arbejds- og familiemæssige baggrund og dit forhold til de mennesker, der står dig nærmest. Herefter gennemgår vi en test, som vi bruger til at følge din udvikling undervejs i forløbet, og vi slutter af med en fysisk undersøgelse og behandling på briksen. Hvis jeg efter undersøgelsen vurderer, at jeg kan hjælpe dig, vil jeg tilbyde dig et andet besøg.
                </p>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Inden da samler jeg mine iagttagelser i en skriftlig rapport, som vi gennemgår sammen. Rapporten beskriver med dine egne ord, hvad du selv mærker, og sætter det i forhold til det, kroppen viser. Den indeholder også min anbefaling til et behandlingsforløb, der er tilpasset dig og dine udfordringer, med en plan for arbejdet med kroppen, tankerne og hverdagen. Hvis du takker ja til forløbet, starter vi op samme dag.
                </p>
                <p className="text-xs text-[#64748B] italic">
                  Alle oplysninger behandles fortroligt og bruges til at planlægge den videre behandling.
                </p>
              </div>

              {/* Fase 3 */}
              <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
                <div>
                  <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">Trin 3</span>
                  <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">Under behandlingsforløbet</h3>
                </div>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Forløbet tilpasses løbende. Vi kombinerer kropsterapiens fysiske forløsning på briksen med coaching og hypnoterapi, alt efter hvad din krop og dit sind er parat til at møde.
                </p>
                <blockquote className="border-l-4 border-[#7c875a] bg-[#FAF8F5] p-5 rounded-r-lg text-sm sm:text-base italic text-[#334155] leading-relaxed">
                  Tryghed er det vigtigste for en god behandling. Derfor tager vi altid udgangspunkt i, hvor du er i livet, og hvad du er klar til at bearbejde og modtage. Processen kan og skal ikke forceres. På samme måde som man ikke kan få en plante til at vokse hurtigere ved at trække i den, kan kroppen ikke slippe en spænding, før den er klar til det.
                </blockquote>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Hver session afsluttes med en kort samtale, hvor vi taler om det, du fik kontakt til under behandlingen. Jeg giver dig input til, hvad du selv kan arbejde videre med derhjemme frem til næste session, enten gennem øvelser eller andre relevante opgaver.
                </p>
              </div>

              {/* Fase 4 */}
              <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
                <div>
                  <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">Trin 4</span>
                  <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">Opfølgning og integration</h3>
                </div>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Et forløb handler ikke kun om at lette det, der fylder lige nu. Det handler om at skabe en tydelig retning for din udvikling. Undervejs følger vi op på testen fra forundersøgelsen, så du kan se, hvordan du gradvist slipper de mønstre, der ikke fungerer, og udvikler nye vaner, der understøtter den forandring, du ønsker dig.
                </p>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Mod slutningen af forløbet samler vi op på det, du har opnået i kroppen, i tankerne og i hverdagen, og ser på, hvordan din indre forandring kommer til udtryk i dit liv, så den bliver ved med at holde. Du går fra forløbet med en bedre forståelse af dig selv og med konkrete redskaber, du kan bruge, når livet udfordrer dig igen, så du kan fortsætte mod det liv, du ønsker.
                </p>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Efter et gennemført forløb kan du efter behov booke enkeltbehandlinger som vedligeholdelse eller opfølgning.
                </p>
              </div>

              {/* Vilkår for forløb */}
              <div className="bg-[#FAF8F5] rounded-2xl p-6 sm:p-8 border border-[#E5E0D8] text-xs sm:text-sm text-[#64748B] space-y-2">
                <h4 className="font-serif font-bold text-base text-[#1b3a4b]">
                  Betingelser for forløb (Handelsbetingelser):
                </h4>
                <p>
                  Betaling for forløb skal ske inden forløbets begyndelse medmindre andet er aftalt.
                </p>
                <p>
                  Forløb sælges som en samlet pakke - det er ikke muligt at få refunderet betaling for forløb.
                </p>
                <p>
                  Aftalte sessioner i forløbet skal aflyses i henhold til gældende afbudspolitik (senest kl. 8 dagen før aftalt tid).
                </p>
              </div>
            </div>

            {/* Aside Contact Form */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 space-y-6">
              <ContactForm />
              <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] text-xs text-[#475569] space-y-2.5 shadow-sm">
                <div className="font-semibold text-[#1b3a4b] text-sm">Direkte kontakt</div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#7c875a]" aria-hidden="true" />
                  <span>Vendersgade 37, 7000 Fredericia</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
                  <a href="tel:53128213" className="hover:text-[#1b3a4b] font-medium">Tlf: 53 12 82 13</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Klientudtalelser */}
      <TestimonialsSlider />
    </div>
  )
}
